// Local payload contracts aligned with Commerce; never import across repositories.
export const DEFINITIONS = Object.freeze({
  stripe: {
    title: 'Stripe 支付',
    type: 'payment',
    realRuntimeId: 'stripe',
    testRuntimeId: 'test-pay',
    realName: 'Stripe',
    fields: [['stripeSecretKey', 'Secret key'], ['stripeWebhookSecret', 'Webhook secret']],
    publicFields: [['stripePublishableKey', 'Publishable key', '用于网页端 Stripe 支付组件。']],
  },
  paypal: {
    title: 'PayPal 支付',
    type: 'payment',
    realRuntimeId: 'paypal',
    testRuntimeId: '',
    realName: 'PayPal',
    fields: [['paypalClientSecret', 'Client secret']],
    publicFields: [['paypalClientId', 'Client ID', '用于浏览器端 PayPal 审核页。'], ['paypalWebhookId', 'Webhook ID', '用于 PayPal Webhook 验签。']],
  },
  wechatpay: {
    title: 'WeChat Pay 支付',
    type: 'payment',
    realRuntimeId: 'wechatpay',
    testRuntimeId: '',
    realName: 'WeChat Pay',
    fields: [['wechatPayMerchantPrivateKey', 'Merchant private key'], ['wechatPayApiV3Key', 'API v3 key']],
    publicFields: [['wechatPayMerchantId', 'Merchant ID', '用于微信支付商户鉴权。'], ['wechatPayAppId', 'App ID', '用于微信支付应用标识。'], ['wechatPayMerchantSerialNumber', 'Merchant serial number', '用于微信平台证书签名验证。']],
  },
  alipay: {
    title: 'Alipay 支付',
    type: 'payment',
    realRuntimeId: 'alipay',
    testRuntimeId: '',
    realName: 'Alipay',
    fields: [['alipayMerchantPrivateKey', 'Merchant private key'], ['alipayPublicKey', 'Alipay public key']],
    publicFields: [['alipayAppId', 'App ID', '用于支付宝应用鉴权。']],
  },
  worldfirst: {
    title: 'WorldFirst 支付',
    type: 'payment',
    realRuntimeId: 'worldfirst',
    testRuntimeId: '',
    realName: 'WorldFirst',
    fields: [['worldfirstPrivateKey', 'RSA private key'], ['worldfirstWebhookSecret', 'Webhook secret']],
    publicFields: [['worldfirstClientId', 'Client ID', 'WorldFirst 应用 Client ID。'], ['worldfirstPublicKey', 'WorldFirst public key', '用于记录公钥版本和接口配置。'], ['worldfirstBaseUrl', 'API base URL', '可选；留空使用当前环境默认地址。']],
  },
  pingpong: {
    title: 'PingPong Checkout 支付',
    type: 'payment',
    realRuntimeId: 'pingpong',
    testRuntimeId: '',
    realName: 'PingPong Checkout',
    fields: [['pingpongSalt', 'API salt'], ['pingpongWebhookSecret', 'Webhook secret']],
    publicFields: [['pingpongAccId', 'Account ID', 'PingPong Checkout 的店铺编号。'], ['pingpongClientId', 'Client ID', 'PingPong Checkout 的商户编号。'], ['pingpongSignType', 'Signature type', '建议填写 SHA256。'], ['pingpongBaseUrl', 'API base URL', '可选；留空按 Dev/Prod 环境使用 PingPong 默认地址。']],
  },
  '17track': {
    title: '17TRACK 自动物流跟踪', type: 'logistics', realRuntimeId: '17track', testRuntimeId: '', realName: '17TRACK',
    fields: [['seventeenTrackApiKey', 'API key（仅服务端保存）']], publicFields: [],
  },
  shippo: {
    title: 'Shippo 物流',
    type: 'logistics',
    realRuntimeId: 'shippo',
    testRuntimeId: 'test-ship',
    realName: 'Shippo',
    fields: [['shippoApiToken', 'API token'], ['commerceWebhookSecret', 'Webhook secret']],
    publicFields: [['probeCarrier', '测试承运商', '用于连接验证。'], ['probeTrackingNumber', '测试单号', '用于连接验证。']],
  },
})

export const NOTIFICATION_EVENTS = [
  ['order.created', '订单创建'],
  ['order.paid', '订单支付成功'],
  ['order.cancelled', '订单取消'],
  ['payment.failed', '支付失败'],
  ['refund.created', '退款创建'],
  ['shipment.created', '物流单创建'],
  ['shipment.in_transit', '运输中'],
  ['shipment.delivered', '已送达'],
  ['shipment.exception', '物流异常'],
]

const field = (form, name) => form[name] ?? ''

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeChannelKind(value) {
  const kind = String(value || '').trim().toLowerCase()
  return kind === 'webhook' || kind === 'outbound_webhook' ? 'webhook' : 'email'
}

function normalizeEmailTransport(value) {
  const transport = String(value || '').trim().toLowerCase()
  if (transport === 'smtp-relay' || transport === 'smtp_relay' || transport === 'smtp') return 'smtp-relay'
  return 'http-relay'
}

function timeoutSecondsFromConfig(config) {
  if (config.timeoutMs !== undefined) return String(Number(config.timeoutMs) / 1000)
  return String(config.timeoutSeconds || 10)
}

export function emptyChannel(environment = 'development', kind = 'email') {
  const normalizedKind = normalizeChannelKind(kind)
  return {
    id: '',
    name: normalizedKind === 'email' ? '订单邮件通知' : '业务事件 Webhook',
    kind: normalizedKind,
    transport: normalizedKind === 'email' ? 'http-relay' : 'webhook',
    environment,
    enabled: false,
    endpoint: '',
    apiKey: '',
    fromAddress: '',
    replyTo: '',
    recipients: '',
    smtpHost: '',
    smtpPort: '587',
    smtpUsername: '',
    smtpPassword: '',
    signingSecret: '',
    timeoutSeconds: '10',
    events: ['order.paid', 'payment.failed', 'shipment.exception'],
  }
}

export function channelToForm(channel, environment) {
  if (!channel) return emptyChannel(environment)

  // publicConfig is the current API shape. config and the flat fields remain
  // readable so channels created by an older Commerce UI do not break editing.
  const config = isRecord(channel.publicConfig)
    ? channel.publicConfig
    : (isRecord(channel.config) ? channel.config : {})
  const kind = normalizeChannelKind(channel.channelType || channel.kind || channel.type)
  const transport = kind === 'email' ? normalizeEmailTransport(channel.transport || config.transport) : 'webhook'
  const hasSecrets = channel.hasSecrets === true

  return {
    ...emptyChannel(environment, kind),
    id: channel.id || channel.channelId || '',
    name: channel.name || config.name || '未命名通知渠道',
    kind,
    transport,
    environment: channel.environment || environment,
    enabled: Boolean(channel.enabled),
    endpoint: kind === 'webhook'
      ? (config.url || channel.targetUrl || channel.endpoint || '')
      : (config.relayUrl || channel.endpoint || ''),
    fromAddress: config.fromAddress || config.from || '',
    replyTo: config.replyTo || '',
    recipients: Array.isArray(config.recipients) ? config.recipients.join(', ') : (config.recipients || ''),
    smtpHost: config.smtpHost || '',
    smtpPort: String(config.smtpPort || 587),
    smtpUsername: config.smtpUsername || '',
    timeoutSeconds: timeoutSecondsFromConfig(config),
    maxRetries: String(config.maxRetries || 5),
    events: Array.isArray(channel.events) ? channel.events : (Array.isArray(config.events) ? config.events : []),
    // The API intentionally returns only hasSecrets, never the secret itself.
    // Use the selected channel type/transport to show the relevant saved state.
    hasSecrets,
    hasApiKey: Boolean(channel.hasApiKey || (hasSecrets && kind === 'email' && transport === 'http-relay')),
    hasSmtpPassword: Boolean(channel.hasSmtpPassword || (hasSecrets && kind === 'email' && transport === 'smtp-relay')),
    hasSigningSecret: Boolean(channel.hasSigningSecret || (hasSecrets && kind === 'webhook')),
  }
}

export function channelPayload(form) {
  const kind = normalizeChannelKind(form.kind || form.channelType)
  const transport = normalizeEmailTransport(form.transport)
  const recipients = field(form, 'recipients').split(/[\n,]/).map((item) => item.trim()).filter(Boolean)
  const publicConfig = kind === 'email' ? {
    transport,
    relayUrl: field(form, 'endpoint').trim(),
    fromAddress: field(form, 'fromAddress').trim(),
    ...(field(form, 'replyTo').trim() ? { replyTo: field(form, 'replyTo').trim() } : {}),
    recipients,
    ...(transport === 'smtp-relay' ? {
      smtpHost: field(form, 'smtpHost').trim(),
      smtpPort: Number(field(form, 'smtpPort') || 587),
      ...(field(form, 'smtpUsername').trim() ? { smtpUsername: field(form, 'smtpUsername').trim() } : {}),
    } : {}),
  } : {
    url: field(form, 'endpoint').trim(),
    timeoutMs: Number(field(form, 'timeoutSeconds') || 10) * 1000,
  }

  const secrets = {}
  if (kind === 'email') {
    if (field(form, 'apiKey')) secrets.apiKey = field(form, 'apiKey')
    if (transport === 'smtp-relay' && field(form, 'smtpPassword')) secrets.smtpPassword = field(form, 'smtpPassword')
  } else if (field(form, 'signingSecret')) {
    secrets.signingSecret = field(form, 'signingSecret')
  }

  return {
    ...(form.id ? { id: form.id } : {}),
    name: field(form, 'name').trim(),
    channelType: kind,
    enabled: Boolean(form.enabled),
    events: Array.isArray(form.events) ? form.events : [],
    publicConfig,
    ...(Object.keys(secrets).length ? { secrets } : {}),
  }
}


export function adminSystemEnvironment(host) {
  const hosts = { 'admin-dev.chamii.com': 'development', 'admin-test.chamii.com': 'test', 'admin.chamii.com': 'production' }
  return Object.hasOwn(hosts, host) ? hosts[host] : null
}

export function assertSystemEnvironment(host, environment) {
  const expected = adminSystemEnvironment(host)
  if (!expected || (environment && environment !== expected)) throw new Error('environment_mismatch')
  return expected
}

export function providerForm(provider, config = {}) {
  const definition = Object.hasOwn(DEFINITIONS, provider) ? DEFINITIONS[provider] : null
  if (!definition) throw new Error('invalid_provider')
  return {
    enabled: config.enabled === true,
    publicConfig: Object.fromEntries(definition.publicFields.map(([key]) => [key, typeof config.publicConfig?.[key] === 'string' ? config.publicConfig[key] : ''])),
    secrets: {},
  }
}

export function providerPayload(provider, form) {
  const definition = Object.hasOwn(DEFINITIONS, provider) ? DEFINITIONS[provider] : null
  if (!definition) throw new Error('invalid_provider')
  const secrets = Object.fromEntries(definition.fields.map(([key]) => [key, String(form.secrets?.[key] || '').trim()]).filter(([, value]) => value))
  return {
    enabled: form.enabled === true,
    ...(definition.publicFields.length ? { publicConfig: Object.fromEntries(definition.publicFields.map(([key]) => [key, String(form.publicConfig?.[key] || '').trim()]).filter(([, value]) => value)) } : {}),
    ...(Object.keys(secrets).length ? { secrets } : {}),
  }
}

export function systemActionPath(kind, id, action = 'save') {
  if (!['save', 'test', 'disable'].includes(action)) throw new Error('invalid_action')
  if (kind === 'provider' && !Object.hasOwn(DEFINITIONS, id)) throw new Error('invalid_provider')
  if (kind === 'notification' && !/^[a-z0-9][a-z0-9._-]{0,127}$/.test(id)) throw new Error('invalid_channel_id')
  if (!['provider', 'notification'].includes(kind)) throw new Error('invalid_kind')
  const base = kind === 'provider' ? `provider-configs/${id}` : 'notification-channels'
  return action === 'save' ? base : `${base}${kind === 'notification' ? `/${id}` : ''}/${action}`
}

export function validatedChannelPayload(form) {
  systemActionPath('notification', form.id)
  const payload = channelPayload(form)
  const url = new URL(form.endpoint)
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('invalid_endpoint')
  if (!payload.name || !payload.events.length || payload.events.some((event) => !NOTIFICATION_EVENTS.some(([name]) => name === event))) throw new Error('invalid_channel')
  if (form.kind === 'webhook' && (!Number.isFinite(payload.publicConfig.timeoutMs) || payload.publicConfig.timeoutMs < 500 || payload.publicConfig.timeoutMs > 15000)) throw new Error('invalid_timeout')
  if (form.transport === 'smtp-relay' && (!Number.isInteger(payload.publicConfig.smtpPort) || payload.publicConfig.smtpPort < 1 || payload.publicConfig.smtpPort > 65535)) throw new Error('invalid_port')
  return payload
}

export function systemTestOutcome(result, notification = false) {
  if (result?.valid === false || result?.success === false || result?.ok === false || ['failed', 'error', 'invalid', 'validation_failed', 'connection_failed'].includes(result?.status || result?.testStatus || result?.connectionStatus)) return 'failed'
  if (notification && result?.actualDelivery === true) return 'delivered'
  return notification ? 'validation_only' : 'completed'
}
